<template>
  <div class="grow">
    <div v-if="isLoading" class="flex justify-center py-12">
      <Spinner class="size-6" />
    </div>

    <StatusBanner v-else-if="isError" variant="danger">{{ t('loadFailed') }}</StatusBanner>

    <template v-else-if="inspection">
      <div class="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2>{{ t('title') }}</h2>
          <p class="text-sm text-obsidian-600">
            {{
              inspection.isLocked
                ? t('lockedText')
                : t('progress', { count: recordedCount, total: shots.length })
            }}
          </p>
        </div>
        <CVButton v-if="!inspection.isLocked" @click="handleOpenCamera()">
          <CameraIcon class="size-4" />
          {{ startLabel }}
        </CVButton>
      </div>

      <StatusBanner v-if="pendingCount" variant="info" class="mb-4">
        {{ t('pendingUploads', { count: pendingCount }) }}
      </StatusBanner>

      <InspectionShotGrid
        :shots="shots"
        :local-previews="localPreviews"
        :fuel-type="fuelType"
        :interactive="!inspection.isLocked"
        @select="handleOpenCamera"
      />

      <StatusBanner v-if="completeError" variant="danger" class="mt-4">
        {{ completeError }}
      </StatusBanner>

      <div class="mt-5 flex items-center justify-end gap-4">
        <span v-if="!isRecorded" class="text-sm text-obsidian-600">
          {{ t('missing', { count: shots.length - recordedCount }) }}
        </span>
        <ButtonNext :is-loading="isCompleting" :disabled="!isRecorded" @click="handleNext">
          {{ t('next') }}
        </ButtonNext>
      </div>

      <InspectionCamera
        v-if="isCameraOpen"
        :shots="cameraShots"
        :initial-key="cameraStartKey"
        :fuel-type="fuelType"
        :capture-shot="handleCaptureShot"
        :skip-shot="handleSkipShot"
        @close="isCameraOpen = false"
        @finished="isCameraOpen = false"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { CameraIcon } from 'lucide-vue-next';
import { InspectionPhase, InspectionShotKey } from '@carvia/ros-client-types';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import InspectionCamera from '@/components/inspections/InspectionCamera.vue';
import InspectionShotGrid from '@/components/inspections/InspectionShotGrid.vue';
import {
  getInspectionShotViews,
  getShotImageUrl,
  isInspectionRecorded,
  countPendingImages,
} from '@/components/inspections/inspection-state';
import { useInspectionLocalPreviews } from '@/components/inspections/use-inspection-local-previews';
import { useProcessNavigation } from '@/hooks/use-process-step-tracking';
import { useBooking } from '@/queries/use-bookings';
import {
  useCompleteInspection,
  useRegisterInspectionCapture,
  useSkipInspectionShot,
  useStartedInspection,
} from '@/queries/use-inspections';
import { evidenceUploadQueue } from '@/utils/evidence-upload-queue';

const props = defineProps<{
  phase: InspectionPhase;
  /** Defaults to the route's booking (`:id`). */
  bookingId?: string;
  /** Defaults to the first allocation for a handover and the last one for a return. */
  allocationId?: string;
  /** Defaults to the next step of the handover or return process. */
  navigateNext?: () => Promise<unknown> | unknown;
}>();

const { t } = useI18n();
const route = useRoute();
const { goToNext } = useProcessNavigation();
const { data: booking } = useBooking(props.bookingId ?? (route.params.id as string));

/** Handover inspects the first allocation; return inspects the car coming back (the last one). */
const allocation = computed(() => {
  const allocations = booking.value?.allocations ?? [];
  if (props.allocationId) return allocations.find(({ id }) => id === props.allocationId);
  return props.phase === InspectionPhase.HANDOVER ? allocations[0] : allocations.at(-1);
});
const fuelType = computed(
  () => allocation.value?.car.fuelType ?? allocation.value?.car.vehicleType.fuelType,
);

const {
  data: inspection,
  isLoading,
  isError,
} = useStartedInspection(
  computed(() => allocation.value?.id),
  props.phase,
);
const { mutateAsync: registerCapture } = useRegisterInspectionCapture();
const { mutateAsync: skipShot } = useSkipInspectionShot();
const { mutateAsync: completeInspection, isPending: isCompleting } = useCompleteInspection();

const shots = computed(() => (inspection.value ? getInspectionShotViews(inspection.value) : []));
const recordedCount = computed(() => shots.value.filter((shot) => shot.state !== 'open').length);
const isRecorded = computed(() => !!inspection.value && isInspectionRecorded(inspection.value));
const pendingCount = computed(() => (inspection.value ? countPendingImages(inspection.value) : 0));
const localPreviews = useInspectionLocalPreviews(inspection);
const cameraShots = computed(() =>
  shots.value.map((view) => ({
    key: view.key,
    state: view.state,
    imageUrl: getShotImageUrl(view, localPreviews.value),
  })),
);

const startLabel = computed(() => {
  if (recordedCount.value === 0) return t('start');
  return isRecorded.value ? t('review') : t('continue');
});

const isCameraOpen = ref(false);
const cameraStartKey = ref<InspectionShotKey>();
const completeError = ref('');

const handleOpenCamera = (shotKey?: InspectionShotKey) => {
  if (inspection.value?.isLocked) return;
  cameraStartKey.value = shotKey;
  isCameraOpen.value = true;
};

const handleCaptureShot = async (shotKey: InspectionShotKey, file: File) => {
  const current = inspection.value;
  if (!current) throw new Error('Inspection missing');
  const captureId = crypto.randomUUID();
  await registerCapture({ id: current.id, shotKey, captureId });
  await evidenceUploadQueue.enqueueInspectionShot({
    inspectionId: current.id,
    shotKey,
    captureId,
    file,
    name: `${shotKey}.jpg`,
  });
};

const handleSkipShot = async (shotKey: InspectionShotKey, reason: string) => {
  if (!inspection.value) throw new Error('Inspection missing');
  await skipShot({ id: inspection.value.id, shotKey, reason });
};

const handleNext = async () => {
  if (!inspection.value || !isRecorded.value) return;
  completeError.value = '';
  // The server checks that every shot is captured or skipped before the process moves on.
  if (!inspection.value.isLocked) {
    try {
      await completeInspection(inspection.value.id);
    } catch {
      completeError.value = t('completeFailed');
      return;
    }
  }
  await (props.navigateNext ?? goToNext)();
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Vehicle photos",
    "progress": "{count} of {total} photos done",
    "lockedText": "These vehicle photos are final. Photos can no longer be changed.",
    "start": "Start photos",
    "continue": "Continue",
    "review": "Retake photos",
    "pendingUploads": "{count} photo is still uploading from this device. | {count} photos are still uploading from this device.",
    "missing": "{count} photo still open | {count} photos still open",
    "next": "Next",
    "loadFailed": "The vehicle photos could not be loaded. Reload the page to try again.",
    "completeFailed": "Could not check the vehicle photos. Check the connection and try again."
  },
  "de": {
    "title": "Fahrzeugfotos",
    "progress": "{count} von {total} Fotos erledigt",
    "lockedText": "Diese Fahrzeugfotos sind abgeschlossen. Fotos können nicht mehr geändert werden.",
    "start": "Fotos aufnehmen",
    "continue": "Weiter aufnehmen",
    "review": "Fotos neu aufnehmen",
    "pendingUploads": "{count} Foto wird noch von diesem Gerät hochgeladen. | {count} Fotos werden noch von diesem Gerät hochgeladen.",
    "missing": "{count} Foto offen | {count} Fotos offen",
    "next": "Weiter",
    "loadFailed": "Die Fahrzeugfotos konnten nicht geladen werden. Seite neu laden und erneut versuchen.",
    "completeFailed": "Die Fahrzeugfotos konnten nicht geprüft werden. Verbindung prüfen und erneut versuchen."
  },
  "es": {
    "title": "Fotos del vehículo",
    "progress": "{count} de {total} fotos hechas",
    "lockedText": "Estas fotos del vehículo están cerradas. Ya no se pueden cambiar.",
    "start": "Hacer fotos",
    "continue": "Continuar",
    "review": "Repetir fotos",
    "pendingUploads": "{count} foto se sigue subiendo desde este dispositivo. | {count} fotos se siguen subiendo desde este dispositivo.",
    "missing": "{count} foto pendiente | {count} fotos pendientes",
    "next": "Siguiente",
    "loadFailed": "No se pudieron cargar las fotos del vehículo. Recarga la página para intentarlo de nuevo.",
    "completeFailed": "No se pudieron comprobar las fotos del vehículo. Comprueba la conexión e inténtalo de nuevo."
  }
}
</i18n>
